import {
  BTQ_APPOINTMENT_BOOKED,
  CHECKOUT_STARTED,
  CONTACT_FORM_SUBMITTED,
  CONTENT_VIEWED,
  emitter,
  LIVECHAT_OPENED,
  NEWSLETTER_SUBSCRIBED,
  PRODUCT_ADDED_TO_CART,
  PRODUCT_FAVOURITED,
  PRODUCT_VIEWED,
  PURCHASE_COMPLETED,
  USER_REGISTERED,
} from '@phoenix/event-bus';

interface NaverConvItem {
  id: string;
  name?: string;
  category?: string;
  quantity?: number;
  payAmount?: string;
}

interface NaverConv {
  type: string;
  value?: string;
  id?: string;
  items?: NaverConvItem[];
  [key: string]: unknown;
}

interface NaverTrackingWindow extends Window {
  wcs?: {
    inflow: (domain: string) => void;
    trans: (conv: Record<string, unknown>) => void;
  };
  wcs_add?: Record<string, string>;
  wcs_do?: (nasa?: Record<string, unknown>) => void;
}

function pushConversion(conv: NaverConv): void {
  const naverWindow = window as unknown as NaverTrackingWindow;
  if (typeof naverWindow.wcs === 'undefined') return;

  const _conv: NaverConv = { type: conv.type };
  if (conv.value) _conv.value = conv.value;
  if (conv.id) _conv.id = conv.id;
  if (conv.items) _conv.items = conv.items;

  naverWindow.wcs.trans(_conv);
}

function toRecord(payload: unknown): Record<string, unknown> {
  return payload !== null && typeof payload === 'object' ? (payload as Record<string, unknown>) : {};
}

function getItemId(item: Record<string, unknown>): string {
  return String(item.reference ?? item.modelReference ?? '');
}

function getItemName(item: Record<string, unknown>): string {
  return String(item.name ?? '');
}

function getItemCategory(item: Record<string, unknown>): string {
  return String(item.collection ?? '');
}

function getItemAmount(item: Record<string, unknown>): string {
  return String(item.price ?? '0');
}

export function initializeNaverTrackingListeners(): void {
  // view_product — fires when a product detail page is viewed
  emitter.on(PRODUCT_VIEWED, (payload: unknown) => {
    const data = toRecord(payload);

    pushConversion({
      type: 'view_product',
      items: [
        {
          id: getItemId(data),
          name: getItemName(data),
          category: getItemCategory(data),
          quantity: 1,
        },
      ],
    });
  });

  // add_to_cart — fires when a product is added to the shopping bag
  emitter.on(PRODUCT_ADDED_TO_CART, (payload: unknown) => {
    const data = toRecord(payload);
    pushConversion({
      type: 'add_to_cart',
      items: [
        {
          id: String(data.reference ?? data.modelReference ?? ''),
          quantity: 1,
        },
      ],
    });
  });

  // add_to_wishlist — fires when a product is favourited
  emitter.on(PRODUCT_FAVOURITED, (payload: unknown) => {
    const data = toRecord(payload);
    pushConversion({
      type: 'add_to_wishlist',
      items: [
        {
          id: String(data.reference ?? data.modelReference ?? ''),
          quantity: 1,
        },
      ],
    });
  });

  // begin_checkout — fires when checkout flow begins
  emitter.on(CHECKOUT_STARTED, (payload: unknown) => {
    const data = toRecord(payload);
    const items = Array.isArray(data.items) ? data.items.map((item) => toRecord(item)) : [];

    pushConversion({
      type: 'begin_checkout',
      items: items.map((item) => ({
        id: getItemId(item),
        name: getItemName(item),
        payAmount: getItemAmount(item),
      })),
    });
  });

  // purchase — fires on order confirmation
  emitter.on(PURCHASE_COMPLETED, (payload: unknown) => {
    const data = toRecord(payload);
    const items = Array.isArray(data.items) ? data.items.map((item) => toRecord(item)) : [];
    pushConversion({
      type: 'purchase',
      value: String(data.total ?? '0'),
      id: String(data.orderId ?? ''),
      items: items.map((item) => ({
        id: getItemId(item),
        name: getItemName(item),
        payAmount: getItemAmount(item),
      })),
    });
  });

  // sign_up — fires on successful account registration
  emitter.on(USER_REGISTERED, () => {
    pushConversion({ type: 'sign_up' });
  });

  // schedule — fires on boutique appointment booking
  emitter.on(BTQ_APPOINTMENT_BOOKED, () => {
    pushConversion({ type: 'schedule' });
  });

  // lead — fires on contact form submission
  emitter.on(CONTACT_FORM_SUBMITTED, () => {
    pushConversion({ type: 'lead' });
  });

  // subscribe — fires on newsletter subscription
  emitter.on(NEWSLETTER_SUBSCRIBED, () => {
    pushConversion({ type: 'subscribe' });
  });

  // view_content — fires on emitter event for CMS-flagged pages
  emitter.on(CONTENT_VIEWED, () => {
    pushConversion({ type: 'view_content' });
  });

  // custom001 — fires when the live chat trigger is clicked
  emitter.on(LIVECHAT_OPENED, () => {
    pushConversion({ type: 'custom001' });
  });
}

initializeNaverTrackingListeners();
